import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { observer } from 'mobx-react-lite';
import reasonListStore from '../../stores/reason-list-store';
import EditorPageLayout from '../../../../components/layouts/editor-page/editor-page-layout';
import { LABEL_POSITIONS, Toggle } from '../../../../components/inputs/toggle/toggle';
import ControlGroup from '../../../../components/layouts/control-group/control-group';
import { FormField } from '../../../../components/form-field/form-field';
import { Input } from '../../../../components/inputs/input/input';
import { SelectDropdown } from '../../../../components/inputs/select-dropdown/select-dropdown';
import TranslationInput from '../../../../translation/translation-input/translation-input';
import usePage from '../../../../components/layouts/base-page-layout/page-hook';
import useTranslation from '../../../../translation/hooks/translation-hook';
import { followUpQuestionTypeList, FOLLOW_UP_QUESTION_TYPE, FOLLOW_UP_QUESTION_TYPE_MAP } from '../../constants';
import QuestionTypeViewer from './question-types/question-type-viewer';
import { ConfirmModal } from '../../../../components/modal/components/confirm-modal/confirm-modal';
import useNavigationBlocker from '../../../../hooks/navigation-blocker-hook';
import useValidationGroup from '../../../../hooks/validation-group/validation-group-hook';


const FollowUpQuestion: React.FC = observer(() => {
    const navigate = useNavigate();
    const vGroup = useValidationGroup();
    const params = useParams();
    const fuqId = params.fuqId;
    const reasonId = params.reasonId as string|number;
    const { setSubTitles } = usePage();
    const { 
        getTranslation, hasChanges: hasTranslationChanges, 
        hasTranslations,
        save: saveTranslations, isSaving: isSavingTranslations,
        discardChanges: translationsDiscardChanges,
        setDeletedKeys, restoreDeletedKeys,
        defaultLocale, selectedLocale, setTranslatableKeys
    } = useTranslation();
    const [isShowUnsaved, setIsShowUnsaved] = useState(false);
    const [targetType, setTargetType] = useState(undefined as FOLLOW_UP_QUESTION_TYPE | undefined);

    const reason = reasonListStore.selectedReturnReason;
    const question = reasonListStore.selectedQuestion;
    const selectedType = FOLLOW_UP_QUESTION_TYPE_MAP[question?.type as FOLLOW_UP_QUESTION_TYPE];

    const translatedSubtitle = getTranslation(reason?.displayName as string);
    useEffect(() => {
        setSubTitles(['Return Reasons', translatedSubtitle, question?.name]);
        
    }, [setSubTitles, translatedSubtitle, question?.name])
    let excludedKeys = [];
    if (question && !question.isImageRequested) {
        excludedKeys.push('imageInstruction');
    }
    const keys = question ? question.getTranslationKeys(excludedKeys) : null;
    useEffect(() => {
        if (keys) {
            setTranslatableKeys(keys);
        }      
        
        return () => setTranslatableKeys([]);
    }, [setTranslatableKeys, keys])

    useEffect(() => {
        reasonListStore.setSelectedReasonId(reasonId);
        if (fuqId) {
            reasonListStore.setSelectedQuestionId(fuqId);
        }
    }, [reasonId, fuqId])

    const onTypeChanged = (type: number) => {
        if (shouldConfirmTypeChange()) {
            setTargetType(type);
        } else {
            question?.setType(type, setDeletedKeys, restoreDeletedKeys);
        }
    }

    const shouldConfirmTypeChange = () => {
        switch (question?.type) {
            case FOLLOW_UP_QUESTION_TYPE.MultiChoiceMultipleAnswer:
            case FOLLOW_UP_QUESTION_TYPE.MultiChoiceSingleAnswer:
            case FOLLOW_UP_QUESTION_TYPE.YesNo:
                let answersTranslationKeys: string[] = [];
                question.answers.forEach(answer => {
                    answersTranslationKeys.push(...answer.getTranslationKeys());
                });

                return hasTranslations(answersTranslationKeys);

            case FOLLOW_UP_QUESTION_TYPE.NumericStepper:
                if (question.stepperHint && hasTranslations([question.stepperHint])) {
                    return true;
                }
                return question.stepperDefaultValue
                        || question.stepperMinValue
                        || question.stepperMaxValue;

            default:
                return false;
        }
    };
    
    const onSaveClick = () => {
        let promised = [];
        if (question?.hasChanges()) {
            promised.push(
                question?.save()
            );   
        }
        if (hasTranslationChanges()) {
            promised.push(
                saveTranslations()
            );
        }
              
        Promise.all(promised).then(() => {  
            unblockNavigation();
            navigate({
                pathname: `/return-reasons/${question?.reasonId}/${question?.id}`,
            }, { replace: true });          
        })
    }

    const discardChanges = () => {
        question?.discardChanges();
        translationsDiscardChanges();
    }

    const hasChanges = (): boolean => {
        return question?.hasChanges() || hasTranslationChanges();
    }

    const { retryNavigation, unblockNavigation } = useNavigationBlocker(
        () => hasChanges(),
        useCallback(() => setIsShowUnsaved(true), [setIsShowUnsaved])
    );

    return (
        <>
        <EditorPageLayout
            toolbar={{ 
                content: (
                    <>
                        <Toggle checked={question?.isEnabled} label="Display question" labelPosition={LABEL_POSITIONS.LEFT} onChange={v => question?.setIsEnabled(v)} />
                        <Toggle checked={question?.isRequired} label="Mandatory question" labelPosition={LABEL_POSITIONS.LEFT} onChange={v => question?.setIsRequired(v)} />
                    </>
                ),
                saveButton: {
                    hasChanges: hasChanges(),
                    onClick: () => onSaveClick(),
                    disabled: !vGroup.isValid() || !hasChanges() || isSavingTranslations(),
                    isValid: vGroup.isValid(),
                }
            }}
        >

            <ControlGroup>
                <FormField label="Internal name" required>
                    <Input
                        placeholder="Internal name"
                        value={question?.name}
                        onChange={v => question?.setName(v)}
                        required
                        validationGroup={vGroup}
                        disabled={selectedLocale !== defaultLocale}
                    />
                </FormField>
                <FormField label="Answer type" required>
                    <SelectDropdown
                        placeholder="Please select"
                        disabled={selectedLocale !== defaultLocale}
                        value={ selectedType }
                        options={ followUpQuestionTypeList() }
                        onChange={ 
                            v => onTypeChanged(v?.value as number) 
                        }
                    />
                </FormField>
                <FormField label="Question" required>
                    <TranslationInput translationKey={question?.question} required validationGroup={vGroup} />
                </FormField>
                <FormField label="Instructions" required>
                    <TranslationInput translationKey={question?.instruction} required validationGroup={vGroup} />
                </FormField>
            </ControlGroup>

            <QuestionTypeViewer question={question} validationGroup={vGroup} />
            
        </EditorPageLayout>
        
        { isShowUnsaved && 
            <ConfirmModal
                cancelButton={{ 
                    title: 'Cancel', 
                    onClick: () => setIsShowUnsaved(false) 
                }} 
                okButton={{ 
                    title: 'Discard', 
                    onClick: () => { 
                        discardChanges();
                        setIsShowUnsaved(false); 
                        retryNavigation();
                    }
                }}
            />
        }

        { targetType &&
            <ConfirmModal
                title={<>Follow-up question - {FOLLOW_UP_QUESTION_TYPE_MAP[question!.type].label}</>}
                text={<>You asked to change the answer type to <strong>{FOLLOW_UP_QUESTION_TYPE_MAP[targetType].label}</strong></>}
                implicationText={<>The settings and content of the current answer will be deleted, including translations</>}
                questionText={<>Are you sure you want to change the answer type?</>}
                cancelButton={{ 
                    title: 'Cancel', 
                    onClick: () => setTargetType(undefined) 
                }} 
                okButton={{ 
                    title: 'Apply', 
                    onClick: () => { 
                        question?.setType(targetType, setDeletedKeys, restoreDeletedKeys);                     
                        setTargetType(undefined); 
                    }
                }}
            />
        }
        </>
    );
})

export default FollowUpQuestion;
